// src/scenes/Dashboard/index.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  Button,
  IconButton,
  Divider,
  List,
  MenuItem,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import MoneyIcon from "@mui/icons-material/Money";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Link } from "react-router-dom";
import { companyManagementUrl, userManagementUrl } from "../../config";
import {
  CustomersCard,
  ExpensesCard,
  RevenueCard,
  RevenueTrend,
  SalesCard,
  SalesTrend,
  SoldProductsChart,
} from "../../data/Axios/queries";
import { graphqlQuery } from "../../data/Axios/DynamicService";
import { tokens } from "../../theme";
import {
  fetchJson,
  fetchJsonDynamic,
} from "../../data/DynamicServiceNew/jsondynamicservice";
import InfoPopover from "./popovercomponent";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../context/TourContext";

const base_url = companyManagementUrl.uri;

// Define tour steps with emphasis on the four quadrants
const dashboardTourSteps = [
  {
    target: ".dashboard-container",
    content: "Welcome to the Dashboard! This is your central hub for monitoring loan activities, performance, collections, and risks for your lending institution.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: ".dashboard-region-filter",
    content: "Use the Region filter to view data for a specific region. This is useful for regional managers to focus on their area.",
    placement: "bottom",
  },
  {
    target: ".dashboard-branch-filter",
    content: "Filter data by Branch to drill down into branch-specific loan metrics.",
    placement: "bottom",
  },
  {
    target: ".dashboard-sales-rep-filter",
    content: "Select a Sales Rep (Field Officer) to view metrics specific to their loan portfolio.",
    placement: "bottom",
  },
  {
    target: ".dashboard-clear-all",
    content: "Click 'Clear All' to reset all filters and view data for the entire company.",
    placement: "bottom",
  },
  {
    target: ".dashboard-portfolio",
    content: "The Portfolio quadrant shows key loan metrics: Active Loans, Total Customers, and Outstanding Loan Balance (OLB). Monitor these to understand the size and health of your loan portfolio.",
    placement: "top",
  },
  {
    target: ".dashboard-performance",
    content: "The Performance quadrant tracks key indicators like Funded Percentage, New Customers, Disbursed Loans, and Collection Rate Month-to-Date (MTD). Use this to assess lending efficiency.",
    placement: "top",
  },
  {
    target: ".dashboard-collection",
    content: "The Collection quadrant provides insights into today's collection activities, including Collection Rate, Prepayment Rate, Loans Due, and Collected Amounts. This helps you manage repayment performance.",
    placement: "top",
  },
  {
    target: ".dashboard-risk",
    content: "The Risk quadrant highlights Portfolio at Risk, Total Arrears, Non-Performing Loans (NPLs), and Turnover Percentage. Use this to identify and mitigate loan risks.",
    placement: "top",
  },
];

const AssetDashboard = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const colors = tokens(theme.palette.mode);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const user = parseInt(decodedToken.RoleGroupId);
  const [branchData, setBranchData] = useState([]);
  const [fieldOfficerfData, setFieldOfficerData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [regionId, setRegionId] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [fieldOfficerId, setFieldOfficerId] = useState(null);
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const circleSize = isSmallScreen ? 150 : 200;

  // Tour state
  const { isFirstTime, touredPages, markPageAsToured } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const currentPath = window.location.pathname;

  useEffect(() => {
    if (isFirstTime && !touredPages.includes(currentPath)) {
      setShowModal(true);
    }
  }, [isFirstTime, touredPages, currentPath]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      markPageAsToured(currentPath);
      setShowModal(false);
    }
  };

  const startTour = () => {
    setRunTour(true);
    setShowModal(false);
  };

  const cancelTour = () => {
    setShowModal(false);
    markPageAsToured(currentPath); // Optionally mark as toured on cancel
  };

  const formattedAmount = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    return amount.toLocaleString("en-US", { minimumFractionDigits: 2 });
  };

  useEffect(() => {
    async function fetchAllData() {
      try {
        const regionDataResponse = await fetchJsonDynamic("region", null, null, base_url);
        if (regionDataResponse?.results) {
          const results = JSON.parse(regionDataResponse.results);
          setRegionData(results || []);
        }
        const branchDataResponse = await fetchJsonDynamic("branch", null, null, base_url);
        if (branchDataResponse?.results) {
          const results = JSON.parse(branchDataResponse.results);
          setBranchData(results || []);
        }
        const userParams = { isActive: 1, roleGroupId: 5 };
        const fieldOfficerDataResponse = await fetchJsonDynamic(
          "allUsersByColumnValues",
          "usermodel",
          userParams,
          userManagementUrl.uri
        );
        if (fieldOfficerDataResponse?.results) {
          const results = JSON.parse(fieldOfficerDataResponse.results);
          const fieldOfficersWithRole5 = results.filter((user) => user.RoleGroupId === 5);
          setFieldOfficerData(fieldOfficersWithRole5 || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchAllData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const dashboardDataResponse = await fetchJsonDynamic(
        "assetDashboardMetricsById",
        "dashboardMetrics",
        { companyid: null, regionid: regionId, branchid: branchId, fieldofficerid: fieldOfficerId },
        base_url
      );
      if (dashboardDataResponse?.results) {
        const results = JSON.parse(dashboardDataResponse.results);
        setDashboardData(results || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [regionId, branchId, fieldOfficerId]);

  const paperStyle = {
    p: 1,
    display: "flex",
    alignItems: "center",
    height: 50,
    backgroundColor: `${colors.primary[400]} !important`,
  };

  const selectStyle = {
    height: "100%",
    "& .MuiSelect-select": { display: "flex", alignItems: "center", padding: "8px" },
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    backgroundColor: `${colors.primary[500]} !important`,
    color: "white",
    padding: "0 10px",
    borderRadius: "4px",
    height: "100%",
    marginRight: "10px",
  };

  const regionOptions = regionData.map((region) => ({
    value: region.Id,
    label: region.Name,
  }));

  const branchOptions = branchData
    .filter((branch) => !regionId || branch.RegionId === regionId)
    .map((branch) => ({ value: branch.Id, label: branch.Name }));

  const fieldOfficerOptions = fieldOfficerfData
    .filter((officer) => !branchId || officer.BranchId === branchId)
    .map((officer) => ({ value: officer.Id, label: `${officer.FirstName} ${officer.LastName}` }));

  const handleRegionChange = (event) => {
    const selectedRegionId = event.target.value;
    setRegionId(selectedRegionId);
    setBranchId(null);
    setFieldOfficerId(null);
  };

  const handleBranchChange = (event) => {
    const selectedBranchId = event.target.value;
    setBranchId(selectedBranchId);
    const selectedBranch = branchData.find((branch) => branch.Id === selectedBranchId);
    if (selectedBranch) setRegionId(selectedBranch.RegionId);
    setFieldOfficerId(null);
  };

  const handleFieldOfficerChange = (event) => {
    const selectedFieldOfficerId = event.target.value;
    setFieldOfficerId(selectedFieldOfficerId);
    const selectedOfficer = fieldOfficerfData.find((officer) => officer.Id === selectedFieldOfficerId);
    if (selectedOfficer) {
      setBranchId(selectedOfficer.BranchId);
      const selectedBranch = branchData.find((branch) => branch.Id === selectedOfficer.BranchId);
      if (selectedBranch) setRegionId(selectedBranch.RegionId);
    }
  };

  const getProgressColor = (value) => {
    if (value <= 40) return "green";
    if (value <= 60) return "#d4a106";
    return "red";
  };

  const getPositiveProgressColor = (value) => {
    if (value <= 40) return "red";
    if (value <= 60) return "#d4a106";
    return "green";
  };

  const handleClearAll = () => {
    setRegionId(null);
    setBranchId(null);
    setFieldOfficerId(null);
    fetchDashboardData();
  };

  return (
    <Box sx={{ p: 3, display: "grid", gridTemplateColumns: "1fr", gridGap: "16px" }} className="dashboard-container">
      {/* Joyride Tour */}
      <Joyride
        steps={dashboardTourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: colors.blueAccent[500],
            zIndex: 1000,
          },
        }}
      />

      {/* First-Time Modal */}
      <Dialog open={showModal} onClose={cancelTour}>
        <DialogTitle>Welcome to the Dashboard!</DialogTitle>
        <DialogContent>
          This dashboard provides a comprehensive overview of your lending operations. Would you like a guided tour to explore key features?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelTour}>Skip</Button>
          <Button onClick={startTour} variant="contained">
            Start Tour
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        {/* Filters */}
        <Grid container spacing={4}>
          {(user === 1 || user === 2 ) && regionData.length > 0 && (
            <Grid item xs={12} md={3} className="dashboard-region-filter">
              <Paper elevation={3} sx={paperStyle}>
                <Typography sx={labelStyle}>
                  <FilterAltIcon style={{ marginRight: "5px" }} />
                  Region
                </Typography>
                <FormControl sx={{ flex: 1, height: "100%" }}>
                  <InputLabel>Select Region...</InputLabel>
                  <Select
                    label="Select Region..."
                    value={regionId || ""}
                    onChange={handleRegionChange}
                    sx={selectStyle}
                  >
                    {regionOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>
          )}
          {(user === 1 || user === 2 || user === 3) && branchData.length > 0 && (
            <Grid item xs={12} md={4} className="dashboard-branch-filter">
              <Paper elevation={3} sx={paperStyle}>
                <Typography sx={labelStyle}>
                  <FilterAltIcon style={{ marginRight: "5px" }} />
                  Branches
                </Typography>
                <FormControl sx={{ flex: 1, height: "100%" }}>
                  <InputLabel>Select Branch...</InputLabel>
                  <Select
                    label="Select Branch..."
                    value={branchId || ""}
                    onChange={handleBranchChange}
                    sx={selectStyle}
                  >
                    {branchOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>
          )}
          {(user === 1 || user === 2 || user === 3 || user === 4) && fieldOfficerfData.length > 0 && (
            <Grid item xs={12} md={4} className="dashboard-sales-rep-filter">
              <Paper elevation={3} sx={paperStyle}>
                <Typography sx={labelStyle}>
                  <FilterAltIcon style={{ marginRight: "5px" }} />
                  Sales Reps
                </Typography>
                <FormControl sx={{ flex: 1, height: "100%" }}>
                  <InputLabel>Select Sales Rep...</InputLabel>
                  <Select
                    label="Select Sales Rep..."
                    value={fieldOfficerId || ""}
                    onChange={handleFieldOfficerChange}
                    sx={selectStyle}
                  >
                    {fieldOfficerOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Paper>
            </Grid>
          )}
          {user !== 5 && (
            <Grid item xs={12} md={1} className="dashboard-clear-all">
              <Button variant="contained" color="secondary" onClick={handleClearAll}>
                Clear All
              </Button>
            </Grid>
          )}
        </Grid>

        {/* Portfolio Quadrant */}
        <Grid item xs={12} md={6} className="dashboard-portfolio">
          <Paper elevation={3} sx={{ p: 2, height: 400, background: `${colors.primary[400]} !important`, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box sx={{ background: theme.palette.primary.main, color: "white", px: 2, py: 1, borderRadius: "0px 10px 10px 0px", flexShrink: 0 }}>
                <Typography variant="h6">Portfolio</Typography>
              </Box>
              <Box sx={{ flexGrow: 1, borderBottom: `2px solid ${theme.palette.primary.main}`, ml: 2 }} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: isSmallScreen ? "column" : "row", gap: isSmallScreen ? 1 : 2, mb: 2 }}>
              <Paper sx={{ p: isSmallScreen ? 1 : 2, display: "flex", alignItems: "center", flex: 1, background: `${colors.primary[200]} !important`, borderRadius: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%", borderBottom: `1px solid ${theme.palette.primary.main}` }}>
                    <IconButton sx={{ backgroundColor: theme.palette.primary.main, color: "white", mr: 2, p: isSmallScreen ? 0.5 : 1, borderRadius: 0 }}>
                      <AccountBalanceIcon />
                    </IconButton>
                    <Typography variant={isSmallScreen ? "h6" : "h5"}>Active Loans</Typography>
                  </Box>
                  <Typography variant={isSmallScreen ? "h4" : "h2"} sx={{ alignSelf: "flex-end" }}>
                    {dashboardData[0]?.Active_Loans || 0}
                  </Typography>
                </Box>
              </Paper>
              <Paper sx={{ p: isSmallScreen ? 1 : 2, display: "flex", alignItems: "center", flex: 1, background: `${colors.primary[200]} !important`, borderRadius: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%", borderBottom: `1px solid ${theme.palette.primary.main}` }}>
                    <IconButton sx={{ backgroundColor: theme.palette.primary.main, color: "white", mr: 2, p: isSmallScreen ? 0.5 : 1, borderRadius: 0 }}>
                      <PeopleIcon />
                    </IconButton>
                    <Typography variant={isSmallScreen ? "h6" : "h5"}>Total Customers</Typography>
                  </Box>
                  <Typography variant={isSmallScreen ? "h4" : "h2"} sx={{ alignSelf: "flex-end" }}>
                    {dashboardData[0]?.Total_Customers || 0}
                  </Typography>
                </Box>
              </Paper>
            </Box>
            <Paper sx={{ p: isSmallScreen ? 1 : 2, display: "flex", alignItems: "center", background: `${colors.primary[200]} !important`, borderRadius: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", width: "100%", borderBottom: `1px solid ${theme.palette.primary.main}` }}>
                  <IconButton sx={{ backgroundColor: theme.palette.primary.main, color: "white", mr: 2, p: isSmallScreen ? 0.5 : 1, borderRadius: 0 }}>
                    <MoneyIcon />
                  </IconButton>
                  <Typography variant={isSmallScreen ? "h6" : "h5"}>OLB(KSH)</Typography>
                </Box>
                <Typography variant={isSmallScreen ? "h4" : "h2"} sx={{ alignSelf: "flex-end" }}>
                  KSH {formattedAmount(dashboardData[0]?.Outstanding_Loan_Balance) || 0}
                </Typography>
              </Box>
            </Paper>
          </Paper>
        </Grid>

        {/* Performance Quadrant */}
        <Grid item xs={12} md={6} className="dashboard-performance">
          <Paper elevation={3} sx={{ p: 2, height: 400, background: `${colors.primary[400]} !important`, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box sx={{ background: theme.palette.primary.main, color: "white", px: 2, py: 1, borderRadius: "0px 10px 10px 0px", flexShrink: 0 }}>
                <Typography variant="h6">Performance</Typography>
              </Box>
              <Box sx={{ flexGrow: 1, borderBottom: `2px solid ${theme.palette.primary.main}`, ml: 2 }} />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "50%" }}>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={circleSize}
                    thickness={7}
                    sx={{ color: "rgba(255, 255, 255, 0.4)", position: "absolute" }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={parseFloat(dashboardData[0]?.Funded_Percentage?.toFixed(2)) || 0}
                    size={circleSize}
                    thickness={7}
                    sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.Funded_Percentage?.toFixed(2)) || 0) }}
                  />
                  <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: "absolute", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <Typography variant="h5" sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.Funded_Percentage?.toFixed(2)) || 0) }}>
                      Funded
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.Funded_Percentage?.toFixed(2)) || 0) }}>
                      {parseFloat(dashboardData[0]?.Funded_Percentage?.toFixed(2)) || 0}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ width: "50%" }}>
                <List sx={{ p: 0 }}>
                  <ListItemButton divider>
                    <ListItemText primary="New Customers" />
                    <Typography variant="h6" sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px" }}>
                      {dashboardData[0]?.New_Customers || 0}
                    </Typography>
                  </ListItemButton>
                  <ListItemButton divider>
                    <ListItemText primary="Disbursed Loans" />
                    <Typography variant="h6" sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px" }}>
                      {formattedAmount(dashboardData[0]?.Disbursed_Loans) || 0}
                    </Typography>
                  </ListItemButton>
                  <ListItemButton divider>
                    <ListItemText primary="Disbursed Amount" />
                    <Typography variant="h6" sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px" }}>
                      {formattedAmount(dashboardData[0]?.Disbursed_Amount) || 0}
                    </Typography>
                  </ListItemButton>
                  <ListItemButton divider>
                    <ListItemText primary="CR MTD" />
                    <Typography variant="h6" sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px" }}>
                      {parseFloat(dashboardData[0]?.Collection_Rate_Mtd_Percentage?.toFixed(2)) || 0}%
                    </Typography>
                  </ListItemButton>
                  <ListItemButton divider>
                    <ListItemText primary="Percentage Mapped" />
                    <Typography variant="h6" sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px" }}>
                      {parseFloat(dashboardData[0]?.Percentage_Mapped?.toFixed(2)) || 0}%
                    </Typography>
                  </ListItemButton>
                </List>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Collection Quadrant */}
        <Grid item xs={12} md={6} className="dashboard-collection">
          <Paper elevation={3} sx={{ p: 2, height: 600, background: `${colors.primary[400]} !important`, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box sx={{ background: theme.palette.primary.main, color: "white", px: 2, py: 1, borderRadius: "0px 10px 10px 0px", flexShrink: 0 }}>
                <Typography variant="h6">Collection</Typography>
              </Box>
              <Box sx={{ flexGrow: 1, borderBottom: `2px solid ${theme.palette.primary.main}`, ml: 2 }} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", mb: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "50%" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "50%", gap: "20px" }}>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={circleSize}
                      thickness={7}
                      sx={{ color: "rgba(255, 255, 255, 0.4)", position: "absolute" }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={parseFloat(dashboardData[0]?.Collection_Rate_Today_Percentage?.toFixed(2)) || 0}
                      size={circleSize}
                      thickness={7}
                      sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.Collection_Rate_Today_Percentage?.toFixed(2)) || 0) }}
                    />
                    <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: "absolute", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                      <Typography variant="h5" sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.Collection_Rate_Today_Percentage?.toFixed(2)) || 0), fontSize: isSmallScreen ? "12px" : "16px" }}>
                        Collection Rate
                      </Typography>
                      <Typography variant="h4" component="div" sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.Collection_Rate_Today_Percentage?.toFixed(2)) || 0), fontSize: isSmallScreen ? "16px" : "24px" }}>
                        {parseFloat(dashboardData[0]?.Collection_Rate_Today_Percentage?.toFixed(2)) || 0}%
                      </Typography>
                      <InfoPopover title="Collection Rate" message="Collected divided by Sum of amount pending collection and collected amount" />
                    </Box>
                  </Box>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={circleSize}
                      thickness={7}
                      sx={{ color: "rgba(255, 255, 255, 0.4)", position: "absolute" }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={parseFloat(dashboardData[0]?.V_prepayment_rate?.toFixed(2)) || 0}
                      size={circleSize}
                      thickness={7}
                      sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.V_prepayment_rate?.toFixed(2)) || 0) }}
                    />
                    <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: "absolute", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                      <Typography variant="h5" sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.V_prepayment_rate?.toFixed(2)) || 0), fontSize: isSmallScreen ? "12px" : "16px" }}>
                        Prepayment Rate
                      </Typography>
                      <Typography variant="h4" component="div" sx={{ color: getPositiveProgressColor(parseFloat(dashboardData[0]?.V_prepayment_rate?.toFixed(2)) || 0), fontSize: isSmallScreen ? "16px" : "24px" }}>
                        {parseFloat(dashboardData[0]?.V_prepayment_rate?.toFixed(2)) || 0}%
                      </Typography>
                      <InfoPopover title="Prepayment Rate" message="Prepaid amount divided by total loans due today" />
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ width: isSmallScreen ? "100%" : "50%", display: "flex", flexDirection: "column", gap: isSmallScreen ? 1 : 2 }}>
                <Box sx={{ border: `1px solid ${theme.palette.primary.main}`, borderRadius: 1, p: isSmallScreen ? 0.5 : 1 }}>
                  <Box sx={{ background: theme.palette.primary.main, color: "white", p: isSmallScreen ? 0.3 : 0.5, borderRadius: 1 }}>
                    <Typography variant={isSmallScreen ? "h6" : "h5"}>Total Loans Due</Typography>
                  </Box>
                  <Typography variant={isSmallScreen ? "body1" : "h6"} sx={{ mt: 0.5 }}>
                    {dashboardData[0]?.V_due_loans_count_today || 0} Loan
                    <InfoPopover title="Total Loans Due Today" message="All the loans that are due today including those that have been paid today or in prepayment" />
                  </Typography>
                  <Typography variant={isSmallScreen ? "h5" : "h4"}>KSH {formattedAmount(dashboardData[0]?.V_total_due_amount) || 0}</Typography>
                  <Box sx={{ background: theme.palette.primary.main, color: "white", p: isSmallScreen ? 0.3 : 0.5, borderRadius: 1, mt: 1 }}>
                    <Typography variant={isSmallScreen ? "h6" : "h5"}>Loans Pending Collection</Typography>
                  </Box>
                  <Typography variant={isSmallScreen ? "body1" : "h6"} sx={{ mt: 0.5 }}>
                    {dashboardData[0]?.Count_Loans_Due_Today || 0} Loan
                    <InfoPopover title="Loans Pending Collection" message="These are loans that are due today but have not been collected by the loan officer" />
                  </Typography>
                  <Typography variant={isSmallScreen ? "h5" : "h4"}>KSH {formattedAmount(dashboardData[0]?.Loans_Due_Today) || 0}</Typography>
                </Box>
                <Box sx={{ border: `1px solid ${theme.palette.primary.main}`, borderRadius: 1, p: isSmallScreen ? 0.5 : 1 }}>
                  <Box sx={{ background: theme.palette.primary.main, color: "white", p: isSmallScreen ? 0.3 : 0.5, borderRadius: 1 }}>
                    <Typography variant={isSmallScreen ? "h6" : "h5"}>Collected</Typography>
                  </Box>
                  <Typography variant={isSmallScreen ? "body1" : "h6"} sx={{ mt: 0.5 }}>
                    {dashboardData[0]?.Count_Collected_Today || 0} Loans
                    <InfoPopover title="Loans Collected" message="These are loans that have been collected by the loan officer today" />
                  </Typography>
                  <Typography variant={isSmallScreen ? "h5" : "h4"}>KSH {formattedAmount(dashboardData[0]?.Collected_Today) || 0}</Typography>
                </Box>
                <Box sx={{ border: `1px solid ${theme.palette.primary.main}`, borderRadius: 1, p: isSmallScreen ? 0.5 : 1 }}>
                  <Box sx={{ background: theme.palette.primary.main, color: "white", p: isSmallScreen ? 0.3 : 0.5, borderRadius: 1 }}>
                    <Typography variant={isSmallScreen ? "h6" : "h5"}>Prepaid Loans due in Future</Typography>
                  </Box>
                  <Typography variant={isSmallScreen ? "body1" : "h6"} sx={{ mt: 0.5 }}>
                    {dashboardData[0]?.Count_Prepaid_Loans_Today || 0} Loans
                    <InfoPopover title="Prepaid Loans due in Future" message="These are loans prepaid today and due in the future" />
                  </Typography>
                  <Typography variant={isSmallScreen ? "h5" : "h4"}>KSH {formattedAmount(dashboardData[0]?.Prepaid_Loans_Today) || 0}</Typography>
                  <Box sx={{ background: theme.palette.primary.main, color: "white", p: isSmallScreen ? 0.3 : 0.5, borderRadius: 1, mt: 1 }}>
                    <Typography variant={isSmallScreen ? "h6" : "h5"}>Prepaid Loans Due Today</Typography>
                  </Box>
                  <Typography variant={isSmallScreen ? "h5" : "h4"}>KSH {formattedAmount(dashboardData[0]?.V_prepaid_amount) || 0}</Typography>
                  <InfoPopover title="Prepaid Loans due today" message="These are loans that were prepaid in the past and are due today" />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Risk Quadrant */}
        <Grid item xs={12} md={6} className="dashboard-risk">
          <Paper elevation={3} sx={{ p: 3, background: `${colors.primary[400]} !important` }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box sx={{ background: theme.palette.primary.main, color: "white", px: 2, py: 1, borderRadius: "0px 10px 10px 0px", flexShrink: 0 }}>
                <Typography variant="h6">Risk</Typography>
              </Box>
              <Box sx={{ flexGrow: 1, borderBottom: `2px solid ${theme.palette.primary.main}`, ml: 2 }} />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, backgroundColor: `${theme.palette.background.default}`, display: "flex", flexDirection: "column", alignItems: "center", height: 200 }}>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={isSmallScreen ? 150 : 150}
                      thickness={6}
                      sx={{ color: "rgba(255, 255, 255, 0.4)", position: "absolute" }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={parseFloat(dashboardData[0]?.Portfolio_At_Risk?.toFixed(2)) || 0}
                      size={isSmallScreen ? 150 : 150}
                      thickness={6}
                      sx={{ color: getProgressColor(parseFloat(dashboardData[0]?.Portfolio_At_Risk?.toFixed(2)) || 0) }}
                    />
                    <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: "absolute", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                      <Typography variant="subtitle2" sx={{ color: getProgressColor(parseFloat(dashboardData[0]?.Portfolio_At_Risk?.toFixed(2)) || 0), fontSize: isSmallScreen ? "10px" : "12px" }}>
                        % Portfolio At Risk
                      </Typography>
                      <Typography variant="h4" sx={{ color: getProgressColor(parseFloat(dashboardData[0]?.Portfolio_At_Risk?.toFixed(2)) || 0) }}>
                        {parseFloat(dashboardData[0]?.Portfolio_At_Risk?.toFixed(2)) || 0}%
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ backgroundColor: `${theme.palette.background.default}`, display: "flex", flexDirection: "column", height: 200 }}>
                  <List sx={{ p: 0 }}>
                    {[
                      { label: "Total Arrears", value: formattedAmount(dashboardData[0]?.Total_Arrears) || 0 },
                      { label: "Arrears Collected Today", value: formattedAmount(dashboardData[0]?.Arrears_Collected_Today) || 0 },
                      { label: "NPL Amount Collected", value: formattedAmount(dashboardData[0]?.Npl_Amount_Collected) || 0 },
                      { label: "RollOver Loans", value: dashboardData[0]?.Roll_Over_Loans || 0 },
                    ].map((item, idx) => (
                      <ListItemButton divider key={idx} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "flex-start" }}>
                          <Typography variant="subtitle2" color="textSecondary">{item.label}</Typography>
                          <Typography variant="body2" sx={{ backgroundColor: theme.palette.primary.main, color: "white", px: 1, borderRadius: "5px", alignSelf: "flex-end" }}>
                            KSH {formattedAmount(item.value)}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Paper elevation={2} sx={{ p: 3, backgroundColor: theme.palette.background.default }}>
                <Typography variant="h6" gutterBottom>Non-Performing Loans Breakdown</Typography>
                <Grid container spacing={2}>
                  {[
                    { title: "Principal Amount in NPL", value: `KSH ${formattedAmount(dashboardData[0]?.Npl_Principal_Amount) || 0}` },
                    { title: "NPL Amount", value: `KSH ${formattedAmount(dashboardData[0]?.Npl_Loan_Balance) || 0}` },
                    { title: "Number of NPLs", value: formattedAmount(dashboardData[0]?.Count_Npl_Loan_Balance) || 0 },
                    { title: "Percentage Turnover", value: `${parseFloat(dashboardData[0]?.Turnover_Percentage?.toFixed(2)) || 0}%` },
                  ].map((item, idx) => (
                    <Grid item xs={12} sm={6} md={6} key={idx}>
                      <Box sx={{ p: 2, border: `1px solid ${theme.palette.primary.main}`, borderRadius: 1, textAlign: "center" }}>
                        <Typography variant="h6" color="textSecondary">{item.title}</Typography>
                        <Typography variant="h4">{item.value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AssetDashboard;